import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Navbar from '../components/Navbar';
import StatCard from '../components/StatCard';
import ImportModal from '../components/ImportModal';

export default function AccountsPage() {
  const [data, setData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImport, setShowImport] = useState(null);
  const [newAccount, setNewAccount] = useState({ name: '', pricePerUnit: '', tenantId: '' });
  const [tenants, setTenants] = useState([]);
  const [savingSite, setSavingSite] = useState(null);
  const [creating, setCreating] = useState(false);
  const [importedData, setImportedData] = useState({ customers: [], prices: [], accounts: [] });
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [accountsRes, monthlyRes] = await Promise.all([
        axios.get('/api/accounts', { headers }),
        axios.get('/api/accounts/monthly', { headers })
      ]);
      setData(accountsRes.data);
      setMonthlyData(monthlyRes.data);
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.clear(); navigate('/login'); }
      setError('Failed to load accounts');
      setLoading(false);
    }
  };

  const fetchImportedData = async () => {
    try {
      const [customersRes, pricesRes, accountsRes] = await Promise.all([
        axios.get('/api/import/customers', { headers }),
        axios.get('/api/import/prices', { headers }),
        axios.get('/api/import/accounts', { headers })
      ]);
      setImportedData({
        customers: customersRes.data.customers,
        prices: pricesRes.data.prices,
        accounts: accountsRes.data.accounts
      });
    } catch (err) {
      console.error('Failed to load imported data');
    }
  };

  useEffect(() => { fetchData(); fetchImportedData(); }, []);

  // Tenant sites (admins only): an account's site decides which paybill its meters are paid into
  useEffect(() => {
    if (user.role !== 'admin') return;
    axios.get('/api/tenants', { headers }).then((r) => setTenants(r.data.tenants || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const siteLabel = (t) => t ? `${t.name} (${t.subdomain})` : 'Main site';

  const changeSite = async (account, value) => {
    const next = value ? tenants.find((t) => String(t.id) === value) : null;
    const msg = `Move "${account.name}" to ${next ? `${next.name} (${next.subdomain}.smartwater.co.ke)` : 'the main site (smartwater.co.ke)'}?\n\n`
      + `Its ${account.meter_count} meter(s) will then only be payable on that site, into ${next ? 'its' : 'the main'} paybill.`;
    if (!window.confirm(msg)) return;
    setSavingSite(account.id);
    try {
      await axios.put(`/api/accounts/${account.id}`, { tenantId: value || null }, { headers });
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change the account’s site');
    } finally {
      setSavingSite(null);
    }
  };

  const handleCreateAccount = async () => {
    if (!newAccount.name.trim()) return;
    setCreating(true);
    try {
      await axios.post('/api/accounts', {
        name: newAccount.name,
        pricePerUnit: newAccount.pricePerUnit || 0,
        ...(user.role === 'admin' ? { tenantId: newAccount.tenantId || null } : {}),
      }, { headers });
      setShowCreateModal(false);
      setNewAccount({ name: '', pricePerUnit: '', tenantId: '' });
      fetchData();
    } catch (err) {
      setError('Failed to create account');
    } finally {
      setCreating(false);
    }
  };

  const handleImportSuccess = () => {
    fetchImportedData();
    setShowImport(null);
  };

  return (
    <div style={{ minHeight: "100vh" }}>
      <Navbar user={user} />

      <div className="mw-page" style={{ padding: "32px 24px", maxWidth: 1200, margin: "0 auto" }}>
        <style>{`
          @keyframes fadeIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>

        {/* Header */}
        <div className="mw-page-head" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 32, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{ color: "#0f172a", fontSize: 26, fontWeight: 700, margin: 0 }}>Accounts</h1>
            <p style={{ color: "#475569", fontSize: 14, marginTop: 6 }}>
              {user.role === 'admin' ? 'All accounts in the system' : 'Your accounts'}
            </p>
          </div>
          <div data-tour="imports" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={() => setShowImport('prices')}
              style={{ background: "#f59e0b", border: "1px solid #d97706", borderRadius: 10, padding: "8px 14px", color: "#1c1917", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
              Import Prices
            </button>
            <button onClick={() => setShowImport('customers')}
              style={{ background: "#1a5fa4", border: "1px solid #1a5fa4", borderRadius: 10, padding: "8px 14px", color: "#ffffff", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
              Import Customers
            </button>
            <button onClick={() => setShowImport('accounts')}
              style={{ background: "#047857", border: "1px solid #047857", borderRadius: 10, padding: "8px 14px", color: "#ffffff", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
              Import Accounts
            </button>
            <button onClick={() => setShowCreateModal(true)}
              style={{ background: "linear-gradient(135deg, #0891b2, #0e7490)", border: "none", borderRadius: 10, padding: "8px 14px", color: "#fff", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
              + New Account
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)", borderRadius: 10, padding: "14px 18px", color: "#f87171", marginBottom: 24 }}>
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", border: "3px solid rgba(56,189,248,0.15)", borderTop: "3px solid #38bdf8", margin: "0 auto 16px", animation: "spin 1s linear infinite" }} />
            <p style={{ color: "#475569" }}>Loading accounts...</p>
          </div>
        )}

        {!loading && data && (
          <>
            {/* Stat cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))", gap: 20, marginBottom: 32, animation: "fadeIn 0.5s ease" }}>
              <StatCard title="Total Accounts" value={data.totalAccounts} icon="" color="#38bdf8" description="Total registered accounts" />
              <StatCard title="Total Transacted" value={`KES ${Number(data.totalTransacted).toLocaleString()}`} icon="" color="#10b981" description="All time total payments" />
            </div>

            {/* Charts */}
            {monthlyData && (
              <div data-tour="account-charts" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 400px), 1fr))", gap: 20, marginBottom: 32, animation: "fadeIn 0.6s ease" }}>
                <div style={{ background: "rgba(15,23,42,0.92)", borderRadius: 16, border: "1px solid rgba(56,189,248,0.15)", padding: 24 }}>
                  <h3 style={{ color: "#ffffff", fontSize: 19, fontWeight: 700, margin: "0 0 20px" }}>Amount Transacted per Month (KES)</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={monthlyData.monthlyAmount}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.25)" />
                      <XAxis dataKey="month" stroke="#94a3b8" tick={{ fill: "#f1f5f9", fontSize: 14, fontWeight: 600 }} />
                      <YAxis stroke="#94a3b8" tick={{ fill: "#f1f5f9", fontSize: 14, fontWeight: 600 }} />
                      <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(56,189,248,0.2)", borderRadius: 8, color: "#e2e8f0" }} formatter={(v) => [`KES ${Number(v).toLocaleString()}`, 'Amount']} />
                      <Bar dataKey="total" fill="#38bdf8" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ background: "rgba(15,23,42,0.92)", borderRadius: 16, border: "1px solid rgba(56,189,248,0.15)", padding: 24 }}>
                  <h3 style={{ color: "#ffffff", fontSize: 19, fontWeight: 700, margin: "0 0 20px" }}>Units Purchased per Month</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={monthlyData.monthlyUnits}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.25)" />
                      <XAxis dataKey="month" stroke="#94a3b8" tick={{ fill: "#f1f5f9", fontSize: 14, fontWeight: 600 }} />
                      <YAxis stroke="#94a3b8" tick={{ fill: "#f1f5f9", fontSize: 14, fontWeight: 600 }} />
                      <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(56,189,248,0.2)", borderRadius: 8, color: "#e2e8f0" }} formatter={(v) => [v, 'Units']} />
                      <Bar dataKey="total_units" fill="#34d399" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Accounts table */}
            <div style={{ background: "rgba(15,23,42,0.92)", borderRadius: 16, border: "1px solid rgba(56,189,248,0.15)", overflow: "hidden", animation: "fadeIn 0.7s ease" }}>
              <div style={{ padding: "20px 24px", borderBottom: "1px solid rgba(56,189,248,0.1)" }}>
                <h3 style={{ color: "#ffffff", fontSize: 19, fontWeight: 700, margin: 0 }}>Accounts List</h3>
              </div>
              {data.accounts.length === 0 ? (
                <div style={{ padding: "48px 24px", textAlign: "center" }}>
                  <p style={{ color: "#cbd5e1", fontSize: 15 }}>No accounts yet</p>
                  <button onClick={() => setShowCreateModal(true)} style={{ marginTop: 16, background: "rgba(56,189,248,0.1)", border: "1px solid rgba(56,189,248,0.25)", borderRadius: 8, padding: "8px 20px", color: "#38bdf8", fontSize: 14.5, cursor: "pointer" }}>
                    Create your first account
                  </button>
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ borderBottom: "1px solid rgba(56,189,248,0.1)" }}>
                        {['Account Name', 'Site (paybill)', user.role === 'admin' ? 'Owner' : null, 'Meters', 'Price/Unit', 'Total Transacted', 'Created'].filter(Boolean).map(h => (
                          <th key={h} style={{ padding: "12px 24px", textAlign: "left", color: "#cbd5e1", fontSize: 13, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 700 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.accounts.map((account, i) => (
                        <tr key={account.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)", background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                          <td style={{ padding: "14px 24px", color: "#e2e8f0", fontSize: 15, fontWeight: 600 }}>{account.name}</td>
                          <td style={{ padding: "10px 24px" }}>
                            {user.role === 'admin' ? (
                              <select aria-label={`Site for ${account.name}`} value={account.tenant_id || ''} disabled={savingSite === account.id}
                                onChange={(e) => changeSite(account, e.target.value)}
                                style={{ background: account.tenant_id ? "rgba(74,155,224,0.18)" : "rgba(255,255,255,0.06)", border: "1.5px solid rgba(148,163,184,0.45)", borderRadius: 8, padding: "7px 10px", color: "#ffffff", fontSize: 14.5, fontWeight: 600, fontFamily: "inherit", maxWidth: 260 }}>
                                <option value="">Main site</option>
                                {tenants.map((t) => <option key={t.id} value={t.id}>{siteLabel(t)}{t.active === false ? ' — inactive' : ''}</option>)}
                              </select>
                            ) : (
                              <span style={{ color: "#ffffff", fontSize: 14.5, fontWeight: 600 }}>{account.tenant_name ? `${account.tenant_name} (${account.tenant_subdomain})` : 'Main site'}</span>
                            )}
                          </td>
                          {user.role === 'admin' && <td style={{ padding: "14px 24px", color: "#e2e8f0", fontSize: 14.5 }}>{account.owner_name}</td>}
                          <td style={{ padding: "14px 24px", color: "#e2e8f0", fontSize: 14.5 }}>{account.meter_count}</td>
                          <td style={{ padding: "14px 24px", color: "#38bdf8", fontSize: 14.5, fontWeight: 600 }}>KES {Number(account.price_per_unit).toLocaleString()}</td>
                          <td style={{ padding: "14px 24px", color: "#10b981", fontSize: 14.5, fontWeight: 600 }}>KES {Number(account.total_transacted).toLocaleString()}</td>
                          <td style={{ padding: "14px 24px", color: "#cbd5e1", fontSize: 13.5 }}>{new Date(account.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Price Items Table */}
            {importedData.prices.length > 0 && (
              <div style={{ background: "rgba(15,23,42,0.92)", borderRadius: 16, border: "1px solid rgba(56,189,248,0.15)", overflow: "hidden", marginTop: 24, animation: "fadeIn 0.5s ease" }}>
                <div style={{ padding: "20px 24px", borderBottom: "1px solid rgba(56,189,248,0.1)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h3 style={{ color: "#ffffff", fontSize: 19, fontWeight: 700, margin: 0 }}>Price Items</h3>
                  <span style={{ color: "#cbd5e1", fontSize: 14.5 }}>{importedData.prices.length} items</span>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ borderBottom: "1px solid rgba(56,189,248,0.1)" }}>
                        {['ID', 'Price', 'Currency', 'Tax'].map(h => (
                          <th key={h} style={{ padding: "12px 20px", textAlign: "left", color: "#cbd5e1", fontSize: 13, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 700 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {importedData.prices.map((p, i) => (
                        <tr key={p.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)", background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                          <td style={{ padding: "12px 20px", color: "#38bdf8", fontSize: 14.5, fontWeight: 600 }}>{p.id}</td>
                          <td style={{ padding: "12px 20px", color: "#10b981", fontSize: 14.5, fontWeight: 600 }}>KES {Number(p.price).toLocaleString()}</td>
                          <td style={{ padding: "12px 20px", color: "#e2e8f0", fontSize: 14.5 }}>{p.currency}</td>
                          <td style={{ padding: "12px 20px", color: "#e2e8f0", fontSize: 14.5 }}>{p.tax}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Customer Accounts Table */}
            {importedData.accounts.length > 0 && (
              <div style={{ background: "rgba(15,23,42,0.92)", borderRadius: 16, border: "1px solid rgba(56,189,248,0.15)", overflow: "hidden", marginTop: 24, animation: "fadeIn 0.5s ease" }}>
                <div style={{ padding: "20px 24px", borderBottom: "1px solid rgba(56,189,248,0.1)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h3 style={{ color: "#ffffff", fontSize: 19, fontWeight: 700, margin: 0 }}>Customer Accounts</h3>
                  <span style={{ color: "#cbd5e1", fontSize: 14.5 }}>{importedData.accounts.length} accounts</span>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ borderBottom: "1px solid rgba(56,189,248,0.1)" }}>
                        {['Customer No', 'Customer Name', 'Meter No', 'Price ID', 'Price', 'Address'].map(h => (
                          <th key={h} style={{ padding: "12px 20px", textAlign: "left", color: "#cbd5e1", fontSize: 13, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 700 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {importedData.accounts.map((a, i) => (
                        <tr key={a.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)", background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                          <td style={{ padding: "12px 20px", color: "#38bdf8", fontSize: 14.5, fontWeight: 600 }}>{a.customer_id}</td>
                          <td style={{ padding: "12px 20px", color: "#e2e8f0", fontSize: 14.5 }}>{a.customer_name}</td>
                          <td style={{ padding: "12px 20px", color: "#e2e8f0", fontSize: 14.5, fontFamily: "monospace" }}>{a.meter_number}</td>
                          <td style={{ padding: "12px 20px", color: "#fbbf24", fontSize: 14.5 }}>{a.price_id}</td>
                          <td style={{ padding: "12px 20px", color: "#10b981", fontSize: 14.5, fontWeight: 600 }}>
                            {a.price ? `KES ${Number(a.price).toLocaleString()}` : '—'}
                          </td>
                          <td style={{ padding: "12px 20px", color: "#cbd5e1", fontSize: 14.5 }}>{a.address || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Account Modal */}
      {showCreateModal && (
        <div className="mw-modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 20 }}>
          <div className="mw-modal" style={{ background: "#0f172a", borderRadius: 16, border: "1px solid rgba(56,189,248,0.2)", padding: 32, width: "100%", maxWidth: 400, animation: "fadeIn 0.3s ease" }}>
            <h3 style={{ color: "#ffffff", fontSize: 20, fontWeight: 700, margin: "0 0 24px" }}>Create New Account</h3>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", color: "#e2e8f0", fontSize: 12.5, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>Account Name</label>
              <input type="text" value={newAccount.name} onChange={e => setNewAccount(a => ({ ...a, name: e.target.value }))} placeholder="e.g. Nairobi North Zone"
                style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.05)", border: "1.5px solid rgba(56,189,248,0.25)", borderRadius: 10, padding: "12px 14px", color: "#e2e8f0", fontSize: 15, outline: "none", fontFamily: "inherit" }} />
            </div>
            {user.role === 'admin' && (
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="new-account-site" style={{ display: "block", color: "#e2e8f0", fontSize: 12.5, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>Site (paybill)</label>
                <select id="new-account-site" value={newAccount.tenantId} onChange={e => setNewAccount(a => ({ ...a, tenantId: e.target.value }))}
                  style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.05)", border: "1.5px solid rgba(56,189,248,0.25)", borderRadius: 10, padding: "12px 14px", color: "#e2e8f0", fontSize: 15, fontFamily: "inherit" }}>
                  <option value="">Main site (smartwater.co.ke)</option>
                  {tenants.filter((t) => t.active !== false).map((t) => <option key={t.id} value={t.id}>{t.name} ({t.subdomain}.smartwater.co.ke)</option>)}
                </select>
                <div style={{ color: "#cbd5e1", fontSize: 13, marginTop: 6 }}>Meters on this account can only be paid for on this site, into its paybill.</div>
              </div>
            )}
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: "block", color: "#e2e8f0", fontSize: 12.5, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>Price per Unit (KES)</label>
              <input type="number" value={newAccount.pricePerUnit} onChange={e => setNewAccount(a => ({ ...a, pricePerUnit: e.target.value }))} placeholder="e.g. 50"
                style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.05)", border: "1.5px solid rgba(56,189,248,0.25)", borderRadius: 10, padding: "12px 14px", color: "#e2e8f0", fontSize: 15, outline: "none", fontFamily: "inherit" }} />
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={() => setShowCreateModal(false)} style={{ flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "12px", color: "#e2e8f0", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button onClick={handleCreateAccount} disabled={creating} style={{ flex: 1, background: "linear-gradient(135deg, #0891b2, #0e7490)", border: "none", borderRadius: 10, padding: "12px", color: "#fff", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
                {creating ? 'Creating...' : 'Create Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modals */}
      {showImport === 'prices' && (
        <ImportModal
          title="Price Management"
          endpoint="/api/import/prices"
          columns={['Id', 'Price', 'Currency', 'Tax']}
          onClose={() => setShowImport(null)}
          onSuccess={handleImportSuccess}
        />
      )}
      {showImport === 'customers' && (
        <ImportModal
          title="Customer Info"
          endpoint="/api/import/customers"
          columns={['Id', 'Name', 'Address', 'Phone']}
          onClose={() => setShowImport(null)}
          onSuccess={handleImportSuccess}
        />
      )}
      {showImport === 'accounts' && (
        <ImportModal
          title="Account Info"
          endpoint="/api/import/accounts"
          columns={['Customer No.', 'Customer Name', 'Meter No.', 'Price Id']}
          onClose={() => setShowImport(null)}
          onSuccess={handleImportSuccess}
        />
      )}
    </div>
  );
}